import { useToast } from '@repo/design-system/index'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import apis, { ai_tutor_apis } from '../../apis'
import { getJwtToken } from '../../auth'
import { axiosGet, axiosPost } from '../../axiosHelper'
import useTranslation from '../../i18n/useTranslation'
import QueryApi from '../../react-query/queryApiClass'
import { IBetaAvailablePricingData } from './types'

interface IBetaAvailablePricingsQueryApiSearchParams {
  locale: string
  platform: string
  eligibility: boolean
  offer_type?: string
  currency?: string
  active?: boolean
  billing_period_unit?: string
  billing_period_number?: number
  acquisition_channel?: string
}

interface IBetaAvailablePricingsQueryApiReturn {
  success: boolean
  pricings: IBetaAvailablePricingData[]
}

const availablePricingsQueryApi = new QueryApi<void, IBetaAvailablePricingsQueryApiSearchParams>({
  api: ai_tutor_apis.pricings.get,
})

export const useAvailablePricings = (
  platform: string,
  billing_period_unit: string,
  eligibility?: boolean,
  offer_type?: string,
  currency?: string,
  active?: boolean,
  billing_period_number?: number,
  acquisition_channel?: string,
  options = {}
) => {
  const { locale } = useTranslation()
  const searchParams = {
    currency,
    locale,
    offer_type,
    active,
    platform,
    eligibility,
    billing_period_unit,
    billing_period_number,
    acquisition_channel,
  }
  return useQuery<IBetaAvailablePricingsQueryApiReturn | any, AxiosError>({
    queryKey: availablePricingsQueryApi.getQueryKey({ searchParams }),
    queryFn: availablePricingsQueryApi.getQueryFn({ searchParams }),
    enabled: !!platform,
    ...options,
  })
}

interface IBetaUserUuidQueryApiReturn {
  uuid: string
}

const userUuidQueryApi = new QueryApi<void>({
  api: ai_tutor_apis.iap_uuid.get,
})

export const useUserUuid = () => {
  return useQuery<IBetaUserUuidQueryApiReturn, AxiosError>({
    queryKey: userUuidQueryApi.getQueryKey(),
    queryFn: userUuidQueryApi.getQueryFn(),
  })
}

interface IBetaIAPProductIDsQueryApiReturn {
  success: boolean
  product_ids: JSON
}

const getIAPProductIDsQueryApi = new QueryApi<void>({
  api: ai_tutor_apis.pricings.product_ids,
})

export const useIAPProductIds = () => {
  return useQuery<IBetaIAPProductIDsQueryApiReturn, AxiosError>({
    queryKey: getIAPProductIDsQueryApi.getQueryKey(),
    queryFn: getIAPProductIDsQueryApi.getQueryFn(),
  })
}

interface IBetaUserTrialEligibilityQueryApiSearchParams {
  platform: string
}

interface IBetaUserTrialEligibilityQueryApiReturn {
  success: boolean
  user_trial_eligibility: JSON
}

const getUserTrialEligibilityQueryApi = new QueryApi<void, IBetaUserTrialEligibilityQueryApiSearchParams>({
  api: ai_tutor_apis.pricings.eligibility,
})

export const useUserTrialEligibility = (platform: string) => {
  const token = getJwtToken()
  const searchParams = {
    platform,
  }
  return useQuery<IBetaUserTrialEligibilityQueryApiReturn, AxiosError>({
    queryKey: getUserTrialEligibilityQueryApi.getQueryKey({ searchParams }),
    queryFn: getUserTrialEligibilityQueryApi.getQueryFn({ searchParams }),
    enabled: !!token && !!platform,
  })
}

export const useAITutorCallPersona = (config = {}) => {
  return useQuery({
    queryKey: [apis.aiTutor.call.persona()],
    queryFn: async () => {
      const response = await axiosGet(apis.aiTutor.call.persona())

      return response
    },
    staleTime: Infinity,
    cacheTime: Infinity,
    ...config,
  })
}

export const useAITutorCallSchedules = (config = {}) => {
  return useQuery({
    queryKey: [apis.aiTutor.call.schedule()],
    queryFn: async () => {
      const response = await axiosGet(apis.aiTutor.call.schedule())
      return response
    },
  })
}

export type TDay = 'Monday' | 'Tuesday' | 'Wednesday' | 'Thursday' | 'Friday' | 'Saturday' | 'Sunday'

export interface UpdateAITutorCallSchedule {
  schedule: Partial<Record<TDay, string[]>> | null
  persona_id: number
  difficulty: 'easy' | 'Intermediate'
  default_duration: 5 | 10
}

export const useUpdateAITutorCallSchedules = () => {
  const queryClient = useQueryClient()
  const { toastError } = useToast()

  return useMutation<any, AxiosError, UpdateAITutorCallSchedule>({
    mutationFn: async (schedule) => {
      const response = await axiosPost(apis.aiTutor.call.schedule(), schedule, {
        shouldThrowError: true,
      })
      return response
    },
    onSuccess: () => {
      queryClient.invalidateQueries([apis.aiTutor.call.schedule()])
    },
    onError: (error) => {
      console.log(error)

      if (error.response?.status === 500) {
        toastError('서버 오류가 발생했습니다. 잠시 후 다시 시도해주세요.')
        return
      }

      toastError('요청 처리 중 오류가 발생했습니다.')
    },
  })
}
